import { useState, useCallback } from 'react';

/**
 * Hook personnalisé pour gérer les dialogs de confirmation
 * Fournit une API simple promise-based pour utiliser ConfirmDialog
 * 
 * @example
 * const confirmDialog = useConfirmDialog();
 * 
 * const handleDelete = async () => {
 *   const confirmed = await confirmDialog.confirm({
 *     title: 'Supprimer la facture',
 *     message: 'Voulez-vous vraiment supprimer cette facture ?',
 *     severity: 'error'
 *   });
 *   
 *   if (confirmed) {
 *     // Procéder à la suppression
 *   }
 * };
 */
const useConfirmDialog = () => {
    const [dialogState, setDialogState] = useState({
        open: false,
        title: '',
        message: '',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        severity: 'warning',
        loading: false,
        resolver: null
    });

    const confirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            setDialogState({
                open: true,
                title: options.title || 'Confirmation',
                message: options.message || 'Êtes-vous sûr de vouloir continuer ?',
                confirmText: options.confirmText || 'Confirmer',
                cancelText: options.cancelText || 'Annuler',
                severity: options.severity || 'warning',
                loading: false,
                resolver: resolve
            });
        });
    }, []);

    const handleConfirm = useCallback(() => {
        if (dialogState.resolver) {
            dialogState.resolver(true);
        }
        setDialogState(prev => ({ ...prev, open: false, resolver: null }));
    }, [dialogState.resolver]);

    const handleCancel = useCallback(() => {
        if (dialogState.resolver) {
            dialogState.resolver(false);
        }
        setDialogState(prev => ({ ...prev, open: false, resolver: null }));
    }, [dialogState.resolver]);

    const setLoading = useCallback((loading) => {
        setDialogState(prev => ({ ...prev, loading }));
    }, []);

    return {
        dialogState,
        confirm,
        handleConfirm,
        handleCancel,
        setLoading
    };
};

export default useConfirmDialog;
